/**
 * Section right-click menu. Opens when the iframe forwards a
 * `request-section-menu` postMessage (right-click landed inside a section
 * but did not hit an editable or a decorative graphic). Surfaces the
 * one Graphics entry whose second level lists selectable targets.
 *
 * Each item emits the SAME `decorative-menu:*` events the direct decorative
 * right-click menu uses, with the same descriptor shape. One action handler
 * module (decorative-action-handlers.js) covers both surfaces.
 *
 * Generic section verbs share the command bar’s capabilities and operations.
 */

import { eventBus } from '../core/events.js';
import { reportError } from '../core/report-error.js';
import { openPopoverMenu } from './popover-menu.js';
import { openSectionReportDialog } from './section-report-dialog.js';
import { templateSiteNameFromProvenancePath } from '../shared/section-identity.js';
import { resolveSectionByInstance } from '../editing/section-utils.js';
import { listDecorativeActionTargets } from '../editing/insert-decorative-svg.js';
import { extractSectionCssBackgroundImages } from '../shared/css-background-images.js';
import { kindLabel } from '../shared/element-kind.js';
import { SECTION_VERBS, sectionCapabilities } from './command-bar/section-command-service.js';
import {
  beginOperation,
  endOperation,
  isOperationActive
} from './command-bar/command-operation-state.js';

let _initialized = false;
let _closeFn = null;

export function initSectionContextMenu() {
  if (_initialized) return;
  _initialized = true;
  eventBus.on('iframe:request-section-menu', _onRequest);
}

export function destroySectionContextMenu() {
  if (!_initialized) return;
  eventBus.off('iframe:request-section-menu', _onRequest);
  _initialized = false;
  _closeActiveMenu();
}

function _onRequest(payload) {
  if (!payload || !payload.instanceId) return;
  openSectionContextMenu(payload);
}

export function openSectionContextMenu(payload) {
  _closeActiveMenu();

  const resolved = resolveSectionByInstance(payload.instanceId);
  if (!resolved || !resolved.section) return;

  const anchorRect = _sectionAnchorRect(payload.point || payload.rect);
  if (!anchorRect) return;

  const targets = collectSectionActionTargets(payload, resolved);
  const triggerEl = _resolveActionTrigger(null);
  const items = _buildItems(targets, payload, resolved, triggerEl);
  if (items.length === 0) {
    _closeActiveMenu();
    return;
  }

  _closeFn = openPopoverMenu(null, {
    anchorRect,
    items,
    align: 'left',
    menuLabel: 'Section actions',
    consumeCanvasDismiss: true,
    onClose: () => {
      _closeFn = null;
    }
  });
}

/**
 * Collect every decorative-action target the section can offer. Combines
 * HTML-based targets (pictures, dividers) from listDecorativeActionTargets
 * with CSS-background targets from extractSectionCssBackgroundImages.
 */
function collectSectionActionTargets(payload, resolved) {
  const targets = listDecorativeActionTargets(resolved.section.html || '', {
    instanceId: payload.instanceId,
    sectionId: payload.sectionId || resolved.section.sectionId || ''
  });

  const cssBackgrounds = extractSectionCssBackgroundImages(
    resolved.section.css || '',
    resolved.section.sectionId || ''
  );
  cssBackgrounds.forEach((bg) => {
    targets.push({
      owner: 'section',
      targetKind: 'decorative-background',
      capabilities: bg.writable ? ['replace', 'remove', 'open-library'] : [],
      instanceId: payload.instanceId,
      sectionId: payload.sectionId || resolved.section.sectionId || '',
      eid: '',
      editableIndex: -1,
      decorativeIndex: null,
      editableType: 'css-background',
      kind: 'background',
      cssBgIndex: bg.index,
      targetRef: {
        domain: 'section-css',
        key: bg.key,
        locator: bg.locator,
        expectedFingerprint: bg.expectedFingerprint
      },
      provenance: bg.provenance,
      decorativeName: bg.index == null ? 'Background (read-only)' : `Background #${bg.index + 1}`,
      tagName: 'css-background',
      cssBackground: true
    });
  });

  return targets;
}

function _buildItems(targets, payload, resolved, triggerEl = null) {
  const caps = sectionCapabilities(payload.instanceId);
  const items = caps
    ? SECTION_VERBS.map((verb) => {
        const operationId = `section:${payload.instanceId}:${verb.id}`;
        const disabledReason =
          caps.disabledReason[verb.id] ||
          (isOperationActive(operationId) ? 'This action is already running' : null);
        return {
          id: verb.id,
          label: verb.label,
          danger: verb.danger,
          disabled: Boolean(disabledReason),
          disabledReason,
          onSelect: async () => {
            const token = beginOperation(operationId, { label: `${verb.label}…` });
            if (token === null) return;
            try {
              await verb.run(payload.instanceId);
            } catch (error) {
              reportError(verb.label, error, { user: 'toast' });
            } finally {
              endOperation(token);
            }
          }
        };
      })
    : [];
  if (targets.length && items.length) items.push({ type: 'separator', id: 'divider-graphics' });
  if (targets.length) {
    items.push({
      id: 'section-graphics',
      label: `Graphics in this section… (${targets.length})`,
      onSelect: () => _openGraphicsList(targets, payload, triggerEl)
    });
  }

  // Tagging the whole section for the agent is available on every section,
  // including ones with no decorative targets — which previously produced no
  // menu at all.
  if (payload?.instanceId) {
    if (items.length > 0) items.push({ type: 'separator', id: 'divider-ai-context' });
    items.push({
      id: 'add-ai-context',
      label: 'Add to AI chat',
      onSelect: () =>
        eventBus.emit('agent:add-context-ref', {
          ref: { kind: 'section', instanceId: payload.instanceId }
        })
    });
  }

  // Reporting a broken section from the canvas — where the defect is actually
  // noticed. TR-0: the dialog, endpoint and triage CLI already existed; for
  // their whole life the only way in was the catalog's code viewer.
  //
  // Gated on catalogKey because the server validates that the key EXISTS in the
  // catalog: an instance with no key (a hand-authored or legacy section) would
  // open a dialog whose Send can only ever 404.
  // Reporting a broken section from the canvas — where the defect is actually
  // noticed. TR-0: the dialog, endpoint and triage CLI already existed; for
  // their whole life the only way in was the catalog's code viewer.
  //
  // Gated on catalogKey because the server validates that the key EXISTS in the
  // catalog: an instance with no key (a hand-authored or legacy section) would
  // open a dialog whose Send can only ever 404.
  const section = resolved?.section;
  if (section?.catalogKey) {
    items.push({ type: 'separator', id: 'divider-report' });
    items.push({
      id: 'report-section',
      label: 'Report a problem…',
      onSelect: () =>
        openSectionReportDialog({
          catalogKey: section.catalogKey,
          description: section.displayName || section.sectionId || '',
          provenance: section.provenancePath || ''
        })
    });

    // TR-3: "it is not this section, the whole crawl is wrong." Offered only
    // when provenance resolves to exactly one template — `unknown` stays
    // unknown, and a shared CodeStitch stitch belongs to no template at all.
    const site = templateSiteNameFromProvenancePath(section.provenancePath || '');
    if (site) {
      items.push({
        id: 'report-template',
        label: `Report the whole template (${site})…`,
        onSelect: () =>
          import('./template-report-dialog.js')
            .then(({ openTemplateReportDialog }) =>
              openTemplateReportDialog({
                templateDomain: site,
                displayName: site,
                sectionHint: section.provenancePath || '',
                // A KEY, never bytes — the server fetches the catalog original.
                attachSection: section.catalogKey
              })
            )
            .catch((err) => reportError('Template report', err, { user: 'toast' }))
      });
    }
  }

  return items;
}

function _openGraphicsList(targets, payload, triggerEl) {
  requestAnimationFrame(() => {
    const anchorRect = _sectionAnchorRect(payload?.point || payload?.rect);
    if (!anchorRect) return;
    const items = targets.map((rawTarget, index) => {
      const target = { ...rawTarget, rect: rawTarget.rect || payload?.rect || null };
      return {
        id: `select-graphic-${index}`,
        label: target.decorativeName || `${kindLabel(target.kind)} ${index + 1}`,
        onSelect: () => eventBus.emit('decorative-menu:select', { target, triggerEl })
      };
    });
    _closeFn = openPopoverMenu(null, {
      anchorRect,
      items,
      align: 'left',
      menuLabel: 'Graphics in this section',
      consumeCanvasDismiss: true,
      onClose: () => {
        _closeFn = null;
      }
    });
  });
}

function _resolveActionTrigger(fallback) {
  return document.getElementById('pb-preview') || fallback || null;
}

function _closeActiveMenu() {
  if (typeof _closeFn === 'function') {
    try {
      _closeFn();
    } catch {
      /* noop */
    }
  }
  _closeFn = null;
}

function _sectionAnchorRect(rect) {
  const iframe = document.getElementById('pb-preview');
  if (!iframe) return null;
  const iframeRect = iframe.getBoundingClientRect();
  const left = iframeRect.left + Number(rect?.left || 0);
  const top = iframeRect.top + Number(rect?.top || 0);
  return { left, top, right: left, bottom: top, width: 0, height: 0 };
}
